import FormContent from "@/_components/FormContent"
import PanelInner from "@/_components/PanelInner"
import { getAuthorizationToken } from "@/_services/axios"
import { getUriAndCacheResponse, resetCache } from "@/_services/cache"
import * as consoleService from "@/_services/console"
import { getLabel, resetLists } from "@/_services/lists"
import * as localizationService from "@/_services/localization"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
import { axiosGetWithCacheSupport } from "@/_services/offlineService"
import { validateSpanishId } from "@/_services/personUtils"
import { resetScripts } from "@/_services/scripts"
import { applyUserConfigurationStyles, removeUserConfigurationStyles } from "@/_services/theming"
import {
  canCheckTenantConfig,
  getConfigAtPath,
  getLoadingDate,
  getOptions,
  getPageConfig,
  getTenant,
  getTimeZone,
  loadUserConfiguration,
  unloadUserConfiguration,
} from "@/_services/userConfiguration"
import axios from "axios"
import {
  dateAdd as _dateAdd,
  dateNoTime as _dateNoTime,
  formatCurrency as _formatCurrency,
  formatDate as _formatDate,
  formatDateTime as _formatDateTime,
  parseFrequencyValue as _parseFrequencyValue,
  coerceStringToJsNumber,
  entityPresenceHeartbeatMs,
  fullStackScripts,
  getContentDispositionFileName,
  getEntityClientRoutes,
  getEntityServerRoute,
  imageAllowedExtensions,
  isNumber,
  isObject,
  isValidIban,
  objectToSearchParam,
  parsePath,
  PROPS_,
  replaceVariables,
  sleep,
  SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION,
  SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION,
  toQueryString,
} from "basikon-common-utils"
import DOMPurify from "dompurify"
import cloneDeep from "lodash.clonedeep"
import debounce from "lodash.debounce"
import get from "lodash.get"
import { marked, Marked } from "marked"
import React from "react"
import { Col, Row } from "react-bootstrap"
import { v4 as uuidv4 } from "uuid"

const { addHook } = DOMPurify
const ContentComponent = React.lazy(() => import("@/_components/ContentComponent.jsx"))

export const globalMessageStyle = {
  fontSize: "16px",
  margin: "30px auto 0",
  textAlign: "center",
}

export const defaultMapCoordinates = [48.864716, 2.349014] /* Paris */
export const htmlTag = document.getElementsByTagName("html")[0]
export const bodyTag = document.getElementsByTagName("body")[0]
export const navCss = {
  varPrefix: "--nav-display-",
  varAllSuffix: "all",
}

export const domAttributes = {
  modelFieldPath: "data-model-field-path",
  modelFieldPathRef: "data-model-field-path-ref",
  isExpanded: "data-is-expanded",
}

export const reservedQueryParams = [
  "jwt",
  "page",
  "token",
  "filter",
  "editMode",
  "parentUrl",
  "selectedIds",
  "noDecoration",
  "navs",
  "userContext",
  "newAuth",
  "clearCache",
]

export const appModes = { FLOW: "flow" }
export const mainViewId = "main-view"
export const entityModelBtnId = "entity-model-btn"
export const virtualKeyboardToggleBtnId = "virtual-keyboard-toggle-btn"
export const sideColumnToggleId = "side-column-toggle"
export const aiViewToggleBtnId = "ai-view-toggle-btn"
export const cardsCollapseToggleId = "collapse-expand-btn"
export const globalSearchBtnId = "global-search-btn"
export const globalSearchInputId = "global-search-input"
export const layoutPageClassName = "pages-layout"
export const reloadDataBtnClassName = "reload-data-btn"
export const entitySaveBtnClassName = "entity-save-btn"
export const pseudoSelectOptionClassName = "pseudo-select-option"
// reserved query params keywords
export const NULL = "NULL"
export const defaultTablePageSize = 20
export const internalDebounce = 250
export const allowedImageUploadTypes = [
  ...new Set(
    imageAllowedExtensions
      .filter(ext => !["svg", "heic", "gif"].includes(ext))
      .map(ext => (ext === "jpg" || ext === "jpeg" ? "image/jpeg" : `image/${ext}`)),
  ),
]

export function getDefaultDebounce() {
  return getOptions("defaultDebounce") ?? 250 // 250ms / 0.25s
}

export const httpHeaders = {
  requestOriginator: "request-originator",
  requestId: "request-id",
  userContexts: "user-contexts",
  contextToken: "context-token",
  paginationContinues: "pagination-continues",
  paginationSize: "pagination-size",
  paginationLimit: "pagination-limit",
  paginationOffset: "pagination-offset",
  paginationAfterId: "pagination-after-id",
  clientTimezone: "client-timezone",
  scriptName: "script-name",
}

export const localStorageKeys = {
  APP_VERSION: "appVersion",
  AUTOVISTA: {
    DATA_TO_FILL: "Autovista.dataToFill",
  },
  ENTRY_POINT_IDP_ID: "entryPointIdpId",
  FIREBASE_TOKEN_PREFIX: "firebaseToken-",
  HEADER_AUTH: "headerAuthorization",
  LOCALE: "locale",
  LOCALES_COMPARISON_PREFIX: "localesComparison",
  MFA_REMEMBER_THIS_DEVICE: "mfaRememberThisDevice",
  HEADER_USER_CONTEXTS: "headerXUserContext",
  HEADER_CONTEXT_TOKEN: "headerXContextToken",
  USER_CURRENT_CONTEXT_INDEX: "userCurrentContextIndex",
  LAST_USER_CONFIGURATION: "lastUserConfiguration",
  OFFLINE_MODE: {
    LIST_PREFIX: "offlineList-",
    STORAGE_NAME: "offlineStorage",
    STORAGE_ERRORS: "offlineStorageErrors",
    STORAGE_VERSION: "offlineStorageVersion",
  },
  OIDC: {
    ID: "oidcId",
    STATE: "oidcState",
    NONCE: "oidcNonce",
    CODE_VERIFIER: "oidcCodeVerifier",
    ORIGINAL_URL: "oidcOriginalUrl",
    LOGOUT_URL: "oidcLogoutUrl",
    REDIRECT_URI: "oidcRedirectUri",
    POST_LOGOUT_REDIRECT_URI: "oidcPostLogoutRedirectUri",
    AUTO: "oidcAuto",
    ID_TOKEN: "oidcIdToken",
    IS_ATTEMPTING_REFRESH: "oidcIsAttemptingRefresh",
  },
  SAML: {
    ID: "samlId",
    SESSION_INDEX: "samlSessionIndex",
    NAME_ID: "samlNameId",
    NAME_ID_FORMAT: "samlNameIdFormat",
  },
  SSO_PARTNER: {
    LOGOUT_URL: "ssoPartnerLogoutUrl",
  },
  UI_STATE: {
    LOCALE_TMP_SWITCH: "localeTmpSwitch",
    ROOT: "uiState",
    FAVICON_URL: "faviconUrl",
    THEMING: {
      NAV_CONTROLS_DISPLAY_MODE: "theming.navControlsDisplayMode",
    },
    SIDE_VIEW: {
      MODE: "sideView.mode",
    },
    AI_CONVERSATION_VIEW: {
      MODE: "aiConversationView.mode",
      IS_OPEN: "aiConversationView.isOpen",
      CURRENT_REGISTRATION: "aiConversationView.currentRegistration",
      AUTO_SEND_AUDIO_RECORDING: "aiConversationView.autoSendAudioRecording",
    },
    CARD: {
      SHOW_SIDE_COLUMN: "Card.showSideColumn",
    },
    PROJECT_TIME_ENTRIES: {
      IS_CALENDAR: "ProjectTimeEntries.isCalendar",
    },
    BUDGET_PAGE: {
      SHOW_DIFF_COLORS: "BudgetPage.showDiffColors",
      IS_YEAR_COLLAPSED: "BudgetPage.isYearCollapsed",
    },
    CONTRACTS_PAGE: {
      IS_KANBAN_VIEW: "ContractsPage.isKanbanView",
    },
    TICKETS_PAGE: {
      IS_KANBAN_VIEW: "TicketsPage.isKanbanView",
      PERSON_FILTER: "TicketsPage.personFilter",
    },
    ASSET_IMAGES: {
      ASSET_DESIGN_MODE: "AssetImages.designMode",
      IMAGE_FIT: "AssetImages.imageFit",
    },
    TASKS_MONITOR: {
      ACTIVATED_CHANNELS_PROFILES: "TaskMonitor.activatedChannelProfiles",
      USER_STATUS: "TaskMonitor.userStatus",
    },
    DOCUMENTS: {
      HIDE_EMPTY_DOCS: "Documents.hideEmptyDocs",
      HIDE_OPTIONAL_DOCS: "Documents.hideOptionalDocs",
      SHOW_METADATA: "Documents.showMetadata",
    },
    SCRIPTS_PAGE: {
      AUTO_PRETTIFY: "ScriptsPage.autoPrettify",
      SHOW_METADATA: "ScriptsPage.showMetadata",
      CODE_EDITOR_THEME: "ScriptsPage.codeEditorTheme",
      SETTINGS_PANEL_WIDTH: "ScriptsPage.settingsPanelWidth",
      EDITOR_PANEL_WIDTH: "ScriptsPage.editorPanelWidth",
      VIEWER_PANEL_WIDTH: "ScriptsPage.viewerPanelWidth",
      SCRIPTS_CONSOLE_HEIGHT: "ScriptsPage.scriptsConsoleHeight",
      SHOW_SCRIPTS_LOGS: "ScriptsPage.showScriptsLogs",
      SHOW_GIT_LOG: "ScriptsPage.showGitLog",
    },
    PE_ASSET_PAGE: {
      IS_DIAGRAM_EXPANDED: "peAssetPage.isDiagramExpanded",
    },
    PANELS_PREFIX: "panels.",
    LOCALE_PAGE: {
      SHOW_TENANT_TRANSLATIONS: "Locale.showTenantTranslations",
      SHOW_CORE_TRANSLATIONS: "Locale.showCoreTranslations",
      SHOW_CORE_TRANSLATIONS_MODIFIED: "Locale.showCoreTranslationsModified",
      SHOW_ONLY_MISSING_TRANSLATIONS: "Locale.showOnlyMissingTranslations",
    },
    NAVS_CARD: {
      PREFIX: "navsCard.",
    },
  },
}

export const booleanSelect = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
]

export const personRoles = {
  CLIENT: "CLIENT",
  PARTNER: "PARTNER",
  SIGNER: "SIGNER",
  COBORROWER: "COBORROWER",
  SUPPLIER: "SUPPLIER",
  SALES: "SALES",
  FUNDER: "FUNDER",
  REPORTER: "REPORTER",
  FOLLOWER: "FOLLOWER",
  REVISER: "REVISER",
  OWNER: "OWNER",
  PAYEE: "PAYEE",
  PAYER: "PAYER",
  BUSINESSUNIT: "BUSINESSUNIT",
  AGENCY: "AGENCY",
}

export function resetServices(args) {
  args = args || {}

  setDebugMode(false)

  resetLists()
  resetScripts()
  resetCache()
  consoleService.reset()
  if (args.logout) {
    localizationService.resetLocale()
  }

  if (args.keepUserConfiguration !== true) {
    unloadUserConfiguration()
    removeUserConfigurationStyles()
  }
}

// legacy, use getDebugMode instead, because importing a variable
// yields random hoisting errors
export let debug = false

export function getDebugMode() {
  return debug
}

function setDebugMode(value) {
  debug = value
}

export function isEmpty(s) {
  return !s
}

export async function downloadFileFromUrl(url, okMessage, koMessage, postData) {
  const response = postData ? await axios.post(url, postData, { responseType: "blob" }) : await axios.get(url, { responseType: "blob" })
  return downloadFileFromResponse(response, okMessage, koMessage)
}

export async function downloadFileFromResponse(response, okMessage /*koMessage*/) {
  try {
    let filename
    const disposition = response.headers["content-disposition"]
    if (disposition) {
      filename = getContentDispositionFileName(disposition)
    } else {
      filename = response.config?.url?.split("/").at(-1)
      const extension = filename?.split(".").at(-1)
      const contentType = response?.headers?.["content-type"]
      if (contentType.includes("pdf") && extension !== "pdf") filename += ".pdf"
      if (contentType.includes("spreadsheet") && extension !== "xlsx") filename += ".xlsx"
      if (contentType.includes("word") && extension !== "docx") filename += ".docx"
    }

    if (!filename) {
      addOops("No file name found")
      return
    }

    downloadDataToFile({ data: response.data, filename })
    addNotification(localizationService.loc(okMessage))
    return true
  } catch (error) {
    // in case of error we get a blob so we need to fix it to display a proper error
    try {
      if (error.response && error.response.data && error.response.data instanceof Blob) {
        let response = await new Response(error.response.data).text()
        let json = JSON.parse(response)
        error.response.data = json
      }
    } catch {} // eslint-disable-line
    addOops(error)
    throw error
  }
}

export function downloadDataToFile({ data, filename }) {
  const blob = new Blob([data])
  const link = document.createElement("a")
  link.href = window.URL.createObjectURL(blob)
  link.download = filename

  document.body.appendChild(link)

  link.click()

  document.body.removeChild(link)
}

export function searchParamToObject(query) {
  let object = {}
  const searchParam = new URLSearchParams(query)
  for (let [field, value] of searchParam) object[field] = value
  return object
}

export function mergeQueryParams(search, params) {
  const query = new URLSearchParams(search)

  // Create obj from query
  const queryParams = {}
  for (let [key, value] of query.entries()) queryParams[key] = value

  // Override with params
  for (let key in params) {
    if (key?.startsWith(PROPS_)) continue
    if (params[key] === "" || params[key] === null) params[key] = undefined
    queryParams[key] = params[key]
  }

  // Remove undefined values
  Object.keys(queryParams).forEach(k => (queryParams[k] === undefined ? delete queryParams[k] : {}))

  // { key: "value" } => key=value
  const mergedQueryParams = Object.keys(queryParams)
    .map(k => `${k}=${encodeURIComponent(queryParams[k])}`)
    .join("&")

  return `?${mergedQueryParams}`
}

export function toArray(str, separator = ",") {
  if (!str) return str
  return str
    .split(separator)
    .map(n => n.trim())
    .filter(n => n)
}

export function applyClasses(classes = {}) {
  return Object.keys(classes)
    .filter(key => key)
    .filter(cls => classes[cls])
    .join(" ")
    .trim()
}

let _organizationsObj
export function getOrganizationsObj() {
  return _organizationsObj || {}
}

export function getOrganizationsValues(isRich) {
  return Object.keys(_organizationsObj).map(key => {
    const value = key === "TENANT" ? "TENANT" : _organizationsObj[key].registration
    return {
      value,
      label: _organizationsObj[key].name + (isRich ? " (" + value + ")" : ""),
    }
  })
}

export function isTenantUseStandard() {
  return getOrganizationsObj()?.["TENANT"]?.useStandard !== false
}

export async function initOrganizationsWithAscendants() {
  try {
    _organizationsObj = await getUriAndCacheResponse(`/api/person/organizations-with-ascendants`, { timeout: 2000 })
  } catch (error) {
    console.warn("Tenant person is not loaded", error)
  }
}

export function copyToClipboard(str) {
  try {
    navigator.clipboard.writeText(str)
  } catch (error) {
    addOops(error)
  }
}

export function convertToYaml(key, obj, indent = "", inArray) {
  let str = []
  if (Array.isArray(obj)) {
    str.push(indent + (key ? key + ": " : ""))
    for (let i = 0; i < obj.length; i++) {
      str.push(...convertToYaml("", obj[i], indent, true))
    }
  } else if (typeof obj === "object") {
    if (obj instanceof Date) {
      str.push(indent + key + ": " + formatDate(obj, localizationService.getLocale()))
    } else if (obj === null) {
      str.push(indent + (key ? key + ": " : "") + JSON.stringify(obj))
    } else {
      key && str.push(indent + key + ":")
      Object.keys(obj).forEach((subKey, i) => {
        str.push(...convertToYaml(subKey, obj[subKey], indent.replace(/-/, " ") + (inArray && i === 0 ? "  - " : "    ")))
      })
    }
  } else {
    str.push(indent + (inArray ? "  - " : "") + (key ? key + ": " : "") + (typeof obj === "string" ? '"' + obj + '"' : obj))
  }
  return str
}

export function toQueryParams(params) {
  const queryParams = {}

  for (const key in params || {}) {
    // Ignore "props_" query params
    if (key.startsWith(PROPS_)) continue

    // Ignore standard query params
    if (reservedQueryParams.includes(key)) continue

    queryParams[key] = params[key]
  }

  return queryParams
}

export function getPaginationParams(headers) {
  return {
    limit: parseInt(headers[httpHeaders.paginationLimit]),
    offset: parseInt(headers[httpHeaders.paginationOffset]),
    size: parseInt(headers[httpHeaders.paginationSize]),
    continues: headers[httpHeaders.paginationContinues] === "true", // type casting to boolean
    afterId: headers[httpHeaders.paginationAfterId],
  }
}

export async function getEntity(entityName, entityRegistration, params, options) {
  const { ignoreError, throwError = true, headers, serverRoute: _serverRoute } = options || {}
  const serverRoute = _serverRoute || getEntityServerRoute(entityName)
  const queryParams = toQueryParams(params)
  const queryString = toQueryString(queryParams)

  try {
    const { data: entity } = await axiosGetWithCacheSupport(`${serverRoute}/${entityRegistration}${queryString}`, { headers })
    return entity
  } catch (error) {
    if (!ignoreError) addOops(error)
    if (throwError) throw error
  }
}

export async function getEntities(
  entityName,
  params,
  { serverRoute, nextPagination, currentBreakdown, typeFieldBreakdownValue, useAdvancedSearchScript } = {},
) {
  if (!serverRoute) serverRoute = getEntityServerRoute(entityName)

  const queryParams = toQueryParams(params)

  if (nextPagination) {
    const { limit, size, offset, afterId } = nextPagination
    const lowerBound = offset + limit
    const upperBound = lowerBound + (currentBreakdown && typeFieldBreakdownValue ? 10 : size)
    queryParams.range = `[${lowerBound}-${upperBound}]`
    if (afterId) queryParams.afterId = afterId
  }

  try {
    if (currentBreakdown && typeFieldBreakdownValue) {
      delete queryParams.breakdown
      queryParams.type = typeFieldBreakdownValue
    }
    const queryString = toQueryString(queryParams)
    const { data, headers } = await axiosGetWithCacheSupport(
      (useAdvancedSearchScript && entityName === "Document" ? `/api/script/runs/advancedSearch-${entityName.toLowerCase()}s` : serverRoute) +
        queryString,
    )

    const paginationParams = getPaginationParams(headers)

    let entities = data
    let breakdown
    if (!Array.isArray(data) && typeof data === "object" && data.entities) {
      entities = data.entities
      breakdown = data.breakdown
    } else if (currentBreakdown && typeFieldBreakdownValue) {
      breakdown = {
        type: {
          ...currentBreakdown.type,
          [typeFieldBreakdownValue]: {
            // after the initial breakdown fetch,
            // the total count is not returned anymore so we have to repeat the one we got initially
            count: currentBreakdown.type[typeFieldBreakdownValue]?.count,
            ...paginationParams,
          },
        },
      }
    }
    return { data: entities, headers, paginationParams, breakdown }
  } catch (error) {
    addOops(error)
  }
  return { data: [] }
}

export async function searchEntities(entityName, search, filters = {}, projection) {
  const serverRoute = getEntityServerRoute(entityName)
  if (!serverRoute) return []

  if (search) search = search.trim()
  if (!search) return []

  let entities = []
  try {
    entities = (await axios.get(serverRoute + toQueryString({ search, ...filters, projection }))).data
  } catch (error) {
    addOops(error)
  }
  return entities
}

export function safeGet(obj, ...args) {
  for (let arg of args) {
    if (!obj) break
    obj = obj[arg]
  }
  return obj
}

/**
 * Attempts to find the MIME type of the supplied base 64 string based on so-called magic-number or magic-byte.
 * This is very unreliable so watch out so do not use that method for critical-mission purposes.
 */
function findBase64UrlMimeType(base64Url) {
  switch (base64Url.charAt(0)) {
    case "/":
      return "jpg"
    case "i":
      return "png"
    case "R":
      return "gif"
    case "U":
      return "webp"
    default:
      return null
  }
}

export function inferDataUriFromBase64(base64) {
  const indexOfBase64Part = base64.indexOf(",")
  if (indexOfBase64Part === -1) {
    const base64Url = base64.substring(indexOfBase64Part + 1)
    const mimeType = findBase64UrlMimeType(base64Url)
    if (mimeType) return `data:${mimeType};base64,${base64Url}`
    // returns nothing if the mime type is not found
  } else {
    // the data uri scheme is a priori already declared
    return base64
  }
}

export async function fileToBase64(file, options) {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const { removeBase64Prefix = true } = options || {}
      resolve(
        removeBase64Prefix
          ? reader.result
              ?.replace("data:image/png;base64,", "")
              .replace("data:image/jpeg;base64,", "")
              .replace("data:image/webp;base64,", "")
              .replace("data:image/svg+xml;base64,", "")
              .replace("data:application/pdf;base64,", "")
          : reader.result,
      )
    }
    reader.onerror = error => console.error("File to base64", error)
  })
}

export function toggleDebugMode(history) {
  const allowDebugMode = getOptions("allowDebugMode") ?? true
  if (!allowDebugMode) return
  setDebugMode(!debug)
  history.replace(history.location.pathname + history.location.search)
  addNotification(localizationService.loc`Debug mode is now ${debug ? "ON" : "OFF"}`, "info")
  window.dispatchEvent(new CustomEvent(customEvents.formInput.enableDebug, { detail: debug }))
}

export function applyPageAdvancedSearchConfig(componentName, searchFields) {
  const pageConfig = getPageConfig(componentName)
  if (pageConfig) {
    const { advancedSearch = {} } = pageConfig
    if (advancedSearch && Object.keys(advancedSearch).length > 0) {
      const advancedSearchProps = Object.fromEntries(Object.entries(advancedSearch).filter(([key]) => key.startsWith("props")))

      for (let advancedSearchProp in advancedSearchProps) {
        for (let i = 0; i < searchFields.length; i++) {
          const searchField = searchFields[i]
          for (let j = 0; j < searchField.length; j++) {
            const { field } = searchField[j]
            if (!field) continue
            if (PROPS_ + field === advancedSearchProp) {
              searchFields[i][j] = {
                ...searchField[j],
                ...advancedSearch[advancedSearchProp],
              }
            }
          }
        }
      }
    }

    const { fields } = advancedSearch || {}
    if (Array.isArray(fields)) {
      fields.map(it => {
        if (!searchFields.includes(it)) searchFields.push(it)
      })
    }
  }
  return searchFields
}

export function checkRegistrationFormat(checkFormatCode, value, regexp) {
  if (!value) return null

  if (checkFormatCode === "iban") {
    if (getOptions("bypassIbanCheck")) return null

    if (!isValidIban(value)) {
      return localizationService.loc`IBAN is invalid`
    }
  }

  if (regexp) {
    if (!value.toString().match(regexp)) {
      return localizationService.loc`Invalid format`
    }
  } else if (["EVA", "EV-"].includes(checkFormatCode)) {
    return checkEvaFormat(value)
  } else if (["SPANISHID"].includes(checkFormatCode?.toUpperCase())) {
    const { type, isValid } = validateSpanishId(value)
    if ((type !== "DNI" && type !== "NIE") || !isValid) return localizationService.loc`Spanish ID is invalid`
  }
  return null
}

function checkEvaFormat(eva) {
  if (!eva) return null
  if (!/^[A-Z][A-Z]/.test(eva)) return localizationService.loc`Must start with two letters`
  if (eva.startsWith("FR")) {
    if (!/^FR[0-9A-Z]{2}[0-9]{9}$/.test(eva)) return "FRXX000000000"
  } else if (eva.startsWith("LT")) {
    if (!/^LT[0-9]{9}$/.test(eva) && !/^LT[0-9]{12}$/.test(eva)) return "LT000000000, LT000000000000"
  }
  // we should complete all EU countries according to https://ec.europa.eu/taxation_customs/vies/faqvies.do#item_11
  return null
}

export function labelFromName(name, { isTranslated = true } = {}) {
  if (!name) return ""
  // name = name.trim() // TODO : Some names (fields) start with a space. So the first letter is not capitalized. Wouldn't it be better to do a trim() before?
  if (name.includes(".")) name = name.split(".").pop() // Support nested field names
  let label = ""
  for (let i = 0; i < name.length; i++) {
    if (i === 0) {
      label += name[0].toUpperCase()
    } else if (name[i] >= "A" && name[i] <= "Z") {
      label += " " + name[i].toLowerCase() // TODO : SEPA => s e p a :-(
    } else {
      label += name[i]
    }
  }
  if (isTranslated) return localizationService.loc([label])
  return label
}

export function urlFromName(str) {
  // ContractLots => contract-lots
  str = str.replace(/[A-Z]/g, (x, d) => (d > 0 ? "-" : "") + x.toLowerCase())
  return str
}

function excelExportConcatChildren(children, result) {
  if (!result) result = ""
  for (const child of children) {
    // $$typeof is a react property, here we want to remove react elements
    if (child && typeof child.$$typeof !== "symbol") {
      if (Array.isArray(child.props?.children)) {
        result += excelExportConcatChildren(child.props.children, result)
      } else if (child.props?.children) {
        result += child.props.children
      } else {
        result += child
      }
    }
  }
  return result
}

function excelExportParseIconItem(item) {
  return item.props?.className?.indexOf("icn-check") !== -1 ? "✓" : "-"
}

/**
 * Columns definitions can have the properties excelWidth and excelFormat to help display nicely the data.
 * For dates, if the field is true date, no need to pass excelFormat = date
 * Standard supported formats by the report service are currency "#,##0.00" and percentage "0.0000%"
 * If you need more can pass directly a pattern, read https://support.microsoft.com/en-us/office/number-format-codes-5026bbd6-04bc-48cd-bf33-80f18b4eae68
 *
 * Sheet names cannot exceed 31 characters (Excel limit).
 *
 * The option removeLastNrows is when you have data with incomplete rows like when displaying totals at the bottom of tables.
 * If you don't remove these rows when opening the file Excel will complain that the file is corrupted and propose an attempt to repair it.
 *
 * React elements aren't totally for export (some are, see the scope covered in the code below about the "definitiveItem").
 * In case you cannot or don't to add a parser, you can set :
 *   on the column that has an issue : excelHidden = true
 *   and create a second column that holds the value you want to export with : hidden = true, excelHidden = false
 */
export async function exportToExcel({ url, data, columns, fileName, sheetName, removeLastNrows = 0, selectedEntities }) {
  if (!data?.length) return

  if (url) {
    await downloadFileFromUrl(url, localizationService.loc`File downloaded`, localizationService.loc`Downloading file`)
    return
  }

  const displayedColumns = columns
    .filter(col => (col?.hidden !== true && col?.excelHidden !== true) || col?.excelHidden === false)
    .map(it => (typeof it === "string" ? { title: labelFromName(it), name: it } : it))
    .map(it => ({ ...it }))
  const locale = localizationService.getLocale()

  if (!sheetName) sheetName = fileName
  // Excel limit is 31 characters for sheet names
  if (sheetName?.length > 31) sheetName = sheetName.substring(0, 31)

  displayedColumns.forEach(dc => {
    if (!dc.excelFormat) {
      if (dc.type === "currency") dc.excelFormat = "currency"
      else if (dc.type === "date") dc.excelFormat = "date"
    }
  })

  if (typeof data === "string" && data?.startsWith("/api")) data = (await axios.get(data)).data

  const arrayOfSelectedEntities = selectedEntities?.size ? Array.from(selectedEntities) : null
  let exportedData = arrayOfSelectedEntities
    ? data.filter(dataItem => {
        return arrayOfSelectedEntities.includes(dataItem.id || dataItem.registration)
      })
    : data

  exportedData = exportedData.map(dataItem => {
    const exportedDataItem = {}

    displayedColumns.forEach(displayedColumn => {
      let foundItem = get(dataItem, displayedColumn.name)
      if (foundItem?.content) foundItem = foundItem.content

      const isFilledMultipleSelect = displayedColumn.multiple && displayedColumn.select && Array.isArray(foundItem)
      const foundChildren = foundItem?.props?.children || (isFilledMultipleSelect ? foundItem : undefined)
      const typeOfFoundItem = typeof foundItem

      let definitiveItem = ""
      if (foundChildren) {
        if (Array.isArray(foundChildren) && !isFilledMultipleSelect) {
          definitiveItem = excelExportConcatChildren(foundChildren)
        } else {
          if (foundChildren.type === "i") {
            definitiveItem = excelExportParseIconItem(foundChildren)
          } else if (foundChildren.props?.field && foundChildren.props?.obj) {
            definitiveItem = foundChildren.props.obj[foundChildren.props.field] || ""
          } else {
            definitiveItem = foundChildren
          }
        }
      } else if (foundItem?.props?.field && foundItem?.props?.obj) {
        definitiveItem = foundItem.props.obj[foundItem.props.field] || ""
      } else if (foundItem?.props?.value) {
        definitiveItem = foundItem.props.value
      } else if (foundItem?.type === "i") {
        definitiveItem = excelExportParseIconItem(foundItem)
      } else if (foundItem?.props?.select) {
        definitiveItem = getLabel(foundItem.props.select, foundItem.props.value)
      } else if (["string", "number", "date", "boolean"].includes(typeOfFoundItem)) {
        definitiveItem = foundItem
      } else if (typeof foundItem?.getMonth === "function") {
        // a date object
        definitiveItem = foundItem //.toLocaleDateString(localizationService.getLocale())
      }
      // the ultimate else would be for item of type react element, which is a type unsuitable for export

      let exportedDataValue = ""
      if (displayedColumn.select) {
        if (isFilledMultipleSelect) {
          definitiveItem.forEach((item, index) => {
            exportedDataValue += (index > 0 ? ", " : "") + getLabel(displayedColumn.select, item)
          })
        } else {
          exportedDataValue = getLabel(displayedColumn.select, definitiveItem)
        }
      } else if (displayedColumn.excelFormat === "currency") {
        exportedDataValue = coerceStringToJsNumber(definitiveItem, locale)
      } else if (typeof definitiveItem === "string") {
        if (foundItem?.props?.select) {
          exportedDataValue = getLabel(foundItem.props.select, definitiveItem)
        } else {
          exportedDataValue = localizationService.loc(definitiveItem)
        }
      } else {
        exportedDataValue = definitiveItem
      }

      exportedDataItem[displayedColumn.name] = exportedDataValue
    })

    return exportedDataItem
  })

  const nbOfRows = exportedData.length
  if (removeLastNrows > 0 && removeLastNrows < nbOfRows) {
    exportedData.splice(nbOfRows - removeLastNrows)
  }

  const excelColumns = {}
  displayedColumns.forEach(({ name, title, excelWidth, excelFormat }) => {
    excelColumns[name] = {
      headerLabel: localizationService.loc(title),
      width: excelWidth,
      format: excelFormat,
    }
  })

  await downloadFileFromUrl("/api/report/excel", localizationService.loc`File downloaded`, localizationService.loc`Downloading file`, {
    fileName,
    [sheetName]: {
      columns: excelColumns,
      data: exportedData,
      autoFilter: true,
    },
    // to add more sheets add more keys like the one above
  })
}

/**
 * Warning: accessing window.parent.location.origin in a cross-origin situation throws an error
 * so we must wrap the code. Below is the exception as shown in Google Chrome.
 * DOMException: Failed to read a named property 'origin' from 'Location': Blocked a frame with origin "someUrl" from accessing a cross-origin frame.
 */
export function isSameOrigin() {
  try {
    return window.location.origin === window.parent.location.origin
  } catch (error) {
    console.warn(error)
  }
}

export function checkIfInIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

let isInSideView
export function setIsInSideView(_isInSideView) {
  isInSideView = _isInSideView
}

export function getIsInSideView() {
  return isInSideView
}

export function openDocInTab({ base64, blob, docName = "" } = {}) {
  if (!base64 && !blob) return

  const openedWindow = window.open()
  if (!openedWindow) return

  openedWindow.document.write(
    `<html><head><title>${docName}</title></head><body style="margin: 0">` +
      `<iframe src="${
        base64 ?? URL.createObjectURL(blob)
      }" frameborder="0" style="border:0; bottom:0; height:100%; left:0; right:0; top:0; width:100%;" allowfullscreen></iframe>` +
      "</body></html>",
  )
}

// "/supplier-asset-lots" => "/asset-lots"
export function getClientAliasRoute(clientRoute) {
  const clientRoutes = getConfigAtPath("clientRoutes")
  if (!clientRoutes) return clientRoute

  for (let key in clientRoutes)
    if (clientRoutes[key].clientRoute === clientRoute && clientRoutes[key].clientAliasRoute) return clientRoutes[key].clientAliasRoute
}

// "/supplier-asset-lots" => "supplierAssetLotsPage"
export function getClientRouteKey(clientRoute) {
  const clientRoutes = getConfigAtPath("clientRoutes")
  if (!clientRoutes) return

  for (const key in clientRoutes) if (clientRoutes[key].clientRoute === clientRoute) return key
}

export async function readBlobAsync(blob, { readAs } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      resolve(readAs === "json" ? JSON.parse(reader.result) : reader.result)
    }
    reader.onerror = reject
    if (["text", "json"].includes(readAs)) {
      reader.readAsText(blob)
    } else {
      reader.readAsDataURL(blob)
    }
  })
}

export function getRandomInt(min = 1, max = 512) {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min) + min) //The maximum is exclusive and the minimum is inclusive
}

export function isPromise(promise) {
  return !!promise && typeof promise.then === "function"
}

export function getQueryParam(props, param) {
  const queryParams = searchParamToObject(props.location.search)
  return queryParams[param]
}

export function getQueryParams(props, params) {
  const queryParams = searchParamToObject(props.location.search)
  return params.map(param => queryParams[param])
}

export function hasQueryParamChanged(props, prevProps, param) {
  const queryParams = searchParamToObject(props.location.search)
  const prevQueryParams = searchParamToObject(prevProps.location.search)

  return queryParams[param] !== prevQueryParams[param]
}

/**
 * @param {Object} param1 Object containing the current search params
 * @param {Object} param2 Object containing the previous search params
 * @param {Array[String]} ignoreParams params to be ignored in addition to the reservedQueryParams
 * @returns True or false depending of whether or not the current and previous params match.
 */
export function hasQueryParamsChanged({ location: { search } }, { location: { search: prevSearch } }, ignoreParams = []) {
  const queryParams = searchParamToObject(search)
  const prevQueryParams = searchParamToObject(prevSearch)

  let params = Object.keys(queryParams).concat(Object.keys(prevQueryParams))
  params = Array.from(new Set(params)) // Remove duplicates
  params = params.filter(param => ![...reservedQueryParams, ...ignoreParams].includes(param))

  for (const param of params) if (queryParams[param] !== prevQueryParams[param]) return true
  return false
}

export function isMonthInterval(fromDate, toDate) {
  if (fromDate?.getDate() !== 1) return
  if (new Date(toDate?.getTime() + 86400000).getDate() !== 1) return
  return fromDate.getFullYear() + fromDate.getMonth() === toDate.getFullYear() + toDate.getMonth()
}

/**
 * This function does not take into account the hour part.
 */
export function isWeekInterval(fromDate, toDate) {
  // humans use week ranges by stating the date of the first day of the week and the date of its last day
  // for instance : the week from 14/03/2022 to 20/03/2022
  // the counting of days is 14, 15, 16, 17, 18, 19, and 20 (7)
  // the difference between two numbers is an interval
  // the week interval is 6 (we are not checking the duration of the week)
  return (new Date(toDate?.toJSON().substr(0, 10)).getTime() - new Date(fromDate?.toJSON().substr(0, 10)).getTime()) / (1000 * 3600 * 24) === 6
}

/**
 * When the app is running in compiled form,
 * the correct displayed version (aka the "external version") has the format "YYYYMMDD-hhmmss vA.B.C"
 * where YYYYMMDD-hhmmss is the build datetime of line 1 of file version.txt
 * and vA.B.C is the release tag inscribed at line 2 of the same file.
 * There is also the "internal version" that comes from the env variables.
 * The internal and external build datetimes can have different values and both are expected
 * to be created externally, typically by the CICD.
 */
export function getAppVersion() {
  const { REACT_APP_BUILD_DATETIME } = process.env
  const storedAppVersion = localStorage.getItem(localStorageKeys.APP_VERSION)
  const buildDatetimeExternal = storedAppVersion?.substring(0, storedAppVersion?.indexOf(" "))
  if (REACT_APP_BUILD_DATETIME) console.debug(`IBT ${REACT_APP_BUILD_DATETIME}`)
  if (buildDatetimeExternal) console.debug(`EBT ${buildDatetimeExternal}`)
  return storedAppVersion
}

export const allDomTags = [
  "a",
  "abbr",
  "acronym",
  "address",
  "applet",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "base",
  "basefont",
  "bdi",
  "bdo",
  "big",
  "blockquote",
  "body",
  "br",
  "button",
  "canvas",
  "caption",
  "center",
  "cite",
  "code",
  "col",
  "colgroup",
  "data",
  "datalist",
  "dd",
  "del",
  "details",
  "dfn",
  "dialog",
  "dir",
  "div",
  "dl",
  "dt",
  "em",
  "embed",
  "fieldset",
  "figcaption",
  "figure",
  "font",
  "footer",
  "form",
  "frame",
  "frameset",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hr",
  "html",
  "i",
  "iframe",
  "img",
  "input",
  "ins",
  "kbd",
  "label",
  "legend",
  "li",
  "link",
  "main",
  "map",
  "mark",
  "meta",
  "meter",
  "nav",
  "noframes",
  "noscript",
  "object",
  "ol",
  "optgroup",
  "option",
  "output",
  "p",
  "param",
  "picture",
  "pre",
  "progress",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "script",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strike",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "svg",
  "table",
  "tbody",
  "td",
  "template",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "title",
  "tr",
  "track",
  "tt",
  "u",
  "ul",
  "var",
  "video",
  "wbr",
]

export function scrollToModelField(modelFieldPath) {
  const targetElement = document.querySelector(`[${domAttributes.modelFieldPath}="${modelFieldPath}"]`)

  // activates nav before scrolling
  const navName = targetElement && findNavNameForElement(targetElement)
  if (navName) {
    activateNav(navName)
    setTimeout(() => dispatchScrollEvent(modelFieldPath), 370)
    return
  }

  // If element not found, try to expand table row if applicable
  if (!targetElement) {
    const wasExpanded = expandTableRowIfNeeded(modelFieldPath)
    if (wasExpanded) {
      // Wait for row to expand and retry scroll
      setTimeout(() => scrollToModelField(modelFieldPath), 370)
      return
    }
  }

  // Fallback - dispatch to all cards immediately
  dispatchScrollEvent(modelFieldPath)
}

/**
 * Finds the nav name that controls the visibility of the given element
 * by traversing up the DOM tree looking for nav CSS variables
 */
function findNavNameForElement(element) {
  let currentElement = element

  while (currentElement && currentElement !== document.body) {
    const styleAttr = currentElement.getAttribute("style")
    if (!styleAttr) {
      currentElement = currentElement.parentElement
      continue
    }

    const navMatches = styleAttr.match(new RegExp(`${navCss.varPrefix.replace("-", "\\-")}([^,)]+)`, "g"))
    if (navMatches?.length > 0) {
      // Find the first specific nav (skip 'all')
      for (const match of navMatches) {
        const name = match.replace(navCss.varPrefix, "")
        if (name !== navCss.varAllSuffix) {
          return name
        }
      }
    }

    currentElement = currentElement.parentElement
  }

  return null
}

/**
 * Activates a specific nav by setting its CSS variable and clearing others
 */
function activateNav(navName) {
  const htmlElement = document.documentElement
  const existingStyle = htmlElement.getAttribute("style")
  if (existingStyle) {
    existingStyle.split(";").forEach(style => {
      const trimmedStyle = style.trim()
      if (trimmedStyle.startsWith(navCss.varPrefix)) {
        htmlElement.style.removeProperty(trimmedStyle.split(":")[0])
      }
    })
  }
  htmlElement.style.setProperty(navCss.varPrefix + navName, "block")

  window.dispatchEvent(new CustomEvent(customEvents.navsCard.syncActiveNav, { detail: { activeNavs: [navName] } }))
}

/**
 * Attempts to expand a table row if the modelFieldPath belongs to an expandible row
 * Returns true if a row was expanded, false otherwise
 */
function expandTableRowIfNeeded(modelFieldPath) {
  // Extract prefix[index] from modelFieldPath
  const match = modelFieldPath.match(/^(.+\[\d+\])\..+$/)
  if (!match) return false

  const prefixWithIndex = match[1]

  // Look for a table expansion button for this prefix[index]
  const expandCell = document.querySelector(`td[${domAttributes.modelFieldPath}="${prefixWithIndex}.expanded"]`)
  if (!expandCell) return false // Not a table row

  const expandButton = expandCell.querySelector("button")
  if (!expandButton) return false

  // Check if it's collapsed (using semantic attribute)
  const isExpanded = expandButton.getAttribute(domAttributes.isExpanded) === "true"
  if (!isExpanded) {
    expandButton.click()
    return true // Row was expanded
  }

  return false // Already expanded or not expandible
}

/**
 * Notifies cards to scroll to a specific model field
 * First tries to find the specific card containing the element,
 * falls back to notifying all cards if not found
 */
function dispatchScrollEvent(modelFieldPath) {
  const event = new CustomEvent(customEvents.card.scrollToModelField, { detail: { modelFieldPath } })

  const targetElement = document.querySelector(`[${domAttributes.modelFieldPath}="${modelFieldPath}"]`)
  if (targetElement) {
    const containingCard = targetElement.closest(".card")
    if (containingCard) {
      containingCard.dispatchEvent(event)
      return
    }
  }

  // Fallback: dispatch to all cards if we couldn't find the specific one
  const cards = document.querySelectorAll(".card")
  for (let i = 0; i < cards?.length; i++) {
    cards[i].dispatchEvent(new CustomEvent(customEvents.card.scrollToModelField, { detail: { modelFieldPath } }))
  }
}

function isEntitySaveBtnEnabled() {
  // Sometimes the workflow card is declared several times in layouts, we only need to use the first one.
  const [entitySaveBtn] = document.querySelectorAll(`.${entitySaveBtnClassName}`)
  return entitySaveBtn?.disabled === false
}

function isEntityPage({ pathname }) {
  const entityClientRoutes = getEntityClientRoutes()
  for (const entityName in entityClientRoutes) {
    const entityRoute = entityClientRoutes[entityName]
    // We need to add a trailing slash to avoid showing the popup when being on entities page,
    // for instance /assets would match /asset but not /asset/ (/asset/:assetId).
    // We also need to match exactly to account for "new" entity pages, for instance /asset
    if (pathname.startsWith(entityRoute + "/") || pathname === entityRoute) {
      return true
    }
  }
}

/**
 * Most browsers prevent using onbeforeunload if the user has not interacted with the page yet
 * so we must create the listener only when we are sure the user has willingly done something.
 * For now we don't use this in Pages and Content.
 */
export function setBeforeUnloadListener() {
  if (window.onbeforeunload) return

  const showConfirmationPopupWhenLeavingUnsavedEntityOption = getOptions("showConfirmationPopupWhenLeavingUnsavedEntity")
  const _showConfirmationPopupWhenLeavingUnsavedEntityOption = Array.isArray(showConfirmationPopupWhenLeavingUnsavedEntityOption)
    ? showConfirmationPopupWhenLeavingUnsavedEntityOption
    : [showConfirmationPopupWhenLeavingUnsavedEntityOption]
  if (!_showConfirmationPopupWhenLeavingUnsavedEntityOption.includes("onWindowUnload")) return

  window.onbeforeunload = () => {
    const { location } = window
    const { pathname } = location
    if (isEntityPage({ pathname }) && isEntitySaveBtnEnabled()) {
      // a string needs to be returned but it is actually not used for display
      return " "
    }
  }
}

/**
 * We need to have the current props so we need to return a function that is called by top level component that
 * can provide them whenever the history changes.
 */
export function getBlockNavigationFunction() {
  const showConfirmationPopupWhenLeavingUnsavedEntityOption = getOptions("showConfirmationPopupWhenLeavingUnsavedEntity")
  const _showConfirmationPopupWhenLeavingUnsavedEntityOption = Array.isArray(showConfirmationPopupWhenLeavingUnsavedEntityOption)
    ? showConfirmationPopupWhenLeavingUnsavedEntityOption
    : [showConfirmationPopupWhenLeavingUnsavedEntityOption]
  if (!_showConfirmationPopupWhenLeavingUnsavedEntityOption.includes("onNavigationAway")) return

  return props => {
    props.history.block(futureLocation => {
      // Warning: the current location is provided by props.location and not history.location.props
      const pathname = props.location.pathname
      if (isEntityPage({ pathname }) && isEntitySaveBtnEnabled() && futureLocation.pathname !== pathname) {
        // Contrary to onbeforeunload, here we have to provide the string to display.
        return loc("Leave the page? Your changes may not be saved.")
      }

      return true
    })
  }
}

// Add page config "fields" to entities
export function applyPageConfigFields(pageConfig, entities) {
  if (!Array.isArray(pageConfig?.fields) || !pageConfig?.fields?.length) return entities

  const fields = pageConfig?.fields.filter(field => field.name && (field.path || field.value || field.url))
  if (!fields.length) return entities

  return entities.map(entity => {
    for (const field of fields) {
      if (field.value) {
        entity[field.name] = field.value
      } else if (field.path) {
        if (field.path.includes("[].")) {
          entity[field.name] = getStringOfArrayValues(entity, field.path)
        } else {
          entity[field.name] = get(entity, parsePath(entity, field.path))
        }
      } else if (field.url) {
        entity[field.name] = replaceVariables(field.url, entity)
      }
    }
    return entity
  })
}

// Ex: "persons[].name" => "John Doe, Jane Doe"
// Can handle nested arrays (limited to 5 levels to avoid illimited recursion)
const MAX_RECURSION = 5
function getStringOfArrayValues(entity, path, counter = 1) {
  let _counter = counter + 1
  if (_counter > MAX_RECURSION) return ""
  const [arrayPath, ...endPath] = path.split("[].")
  if (!endPath.length) return get(entity, parsePath(entity, path))
  const array = get(entity, parsePath(entity, arrayPath))
  if (!array?.length) return ""
  if (array.length === 1) return getStringOfArrayValues(array[0], endPath.join("[]."), _counter)
  return array
    .map(it => (getStringOfArrayValues(it, endPath.join("[]."), _counter) || "").toString())
    .filter(it => it)
    .join(", ")
}

export const selfIframeSources = {
  SIDE_VIEW: "SIDE_VIEW",
}

export const selfIframeMessages = {
  READY: "READY",
}

export const customEvents = {
  card: {
    toggle: "card:toggle",
    scrollToModelField: "card:scroll-to-model-field",
    openTabCallback: "card:open-tab-callback",
  },
  formInput: {
    forceComponentUpdate: "form-input:forceComponentUpdate",
    enableDebug: "form-input:enableDebug",
  },
  innerPanel: {
    open: "inner-panel:open",
    toggle: "inner-panel:toggle",
  },
  personComponent: {
    openTab: "persons-component:open-tab",
    layoutRefresh: "persons-component:layout-refresh",
  },
  navsCard: {
    displayAllNavs: "navs-card:display-all-navs",
    syncActiveNav: "navs-card:sync-active-nav",
    requestActiveNav: "navs-card:request-active-nav",
  },
  user: {
    photoHasChanged: "user:photo-has-changed",
  },
  pagesLayout: {
    reloadRoute: "pages-layout:reload-route",
  },
  pageFrame: {
    emptyNavControls: "page-frame:empty-nav-controls",
    userHasChangedOwnLocale: "page-frame:user-has-changed-own-locale",
  },
  aiConversationView: {
    toggleView: "ai-conversation-view:toggle-view",
  },
  sideView: {
    setUrl: "side-view:set-url",
    toggleView: "side-view:toggle-view",
    softReload: "side-view:soft-reload",
  },
  globalSearch: {
    toggle: "global-search:toggle",
  },
  documents: {
    refresh: "documents:refresh",
  },
}

/**
 * @param {Object} params
 * @param {Function} params.getField Function generating fields
 * @param {Array[]} params.rows Array of array representing rows containing cols containing fields
 * @param {Object} params.entity Entity of the fields
 * @param {function} params.handleSetState Function to set the entity state for the fields
 * @param {string} params.readOnly readOnly of the entity
 * @param {string} params.modelPath Model path of the entity
 * @param {number} [params.index] Optional index if the entity is part of an array
 * @param {Object} [params.handlers] Optional handlers to update field outside of the entity
 * @returns Array of <Row></Row>
 */
export function generateRows({ getField, rows, entity, handleSetState, readOnly, modelPath, index, handlers, cardProps }) {
  if (typeof rows === "string") rows = [[rows]]
  return rows.map((row, _index) => (
    <Row key={_index}>{generateCols({ getField, row, entity, handleSetState, readOnly, modelPath, index, handlers, cardProps })}</Row>
  ))
}

function generateCols({ getField, row, entity, handleSetState, readOnly, modelPath, index, handlers, cardProps }) {
  const params = { getField, entity, handleSetState, readOnly, modelPath, index, handlers, cardProps }
  if (typeof row === "string") {
    // the field handles the <Col> itself
    return generateCell({ cell: { name: row }, ...params })
  } else if (Array.isArray(row)) {
    row = row.filter(it => it)
    const defaultColProps =
      row.length === 1 ? { xs: 12 } : row.length === 2 ? { xs: 12, sm: 6 } : row.length === 3 ? { xs: 12, sm: 6, md: 4 } : { xs: 12, sm: 6, md: 3 }
    return row.map((col, key) => {
      if (typeof col === "string") {
        // the field handles the <Col> itself
        return generateCell({ cell: { name: col }, ...params })
      } else if (Array.isArray(col)) {
        return (
          <Col key={key} {...defaultColProps}>
            {generateRows({ rows: col, ...params })}
          </Col>
        )
      } else if (typeof col === "object") {
        if (col.rows) {
          // an object { colProps, rows }, allows to change the colProps
          return (
            <Col key={key} {...(col.colProps || defaultColProps)} className={col.className || ""}>
              {generateRows({ rows: col.rows, ...params })}
            </Col>
          )
        } else {
          // the field handles the <Col> itself
          return generateCell({ cell: col, ...params })
        }
      } else {
        // assume null
        return null
      }
    })
  } else if (row) {
    const { title, titleLinkTo, collapse, rows, name, className } = row
    if (name) return generateCell({ cell: row, ...params })
    const content = Array.isArray(rows) ? generateRows({ rows, ...params }) : null
    // formInputProps & buttonProps are provided without title
    // They aren't put in a panel so that their layout can controlled finely by configuration.
    return title ? (
      <Col xs={12}>
        <PanelInner collapse={collapse} title={title} titleLinkTo={titleLinkTo} className={className}>
          {content}
        </PanelInner>
      </Col>
    ) : (
      content
    )
  }
}

function generateCell({ getField, cell, entity, handleSetState, readOnly, modelPath, index, handlers, cardProps }) {
  const inputEvents = ["action", "onBlur", "onFocus"]
  for (const eventName of inputEvents) {
    const eventFunction = cell.formInputProps?.[eventName]
    if (typeof eventFunction === "function") {
      cell.formInputProps[eventName] = async event => {
        const eventPatch = await eventFunction({ event, pageState: cloneDeep(entity) })
        if (eventPatch) handleSetState(eventPatch)
      }
    }
  }
  if (cell.name === "spacer")
    return (
      <Col key={uuidv4()} xs={12}>
        <hr></hr>
      </Col>
    )
  let cellComponent = getField(cell, entity, handleSetState, index, cardProps)
  if (!cellComponent) {
    const { name, formInputProps } = cell
    if (!name && formInputProps) {
      const key = [cell.title, formInputProps?.field, formInputProps?.label, cell.buttonProps?.uri, cell.buttonProps?.label].filter(it => it).join("")
      cellComponent = (
        <FormContent
          key={key}
          obj={entity}
          field={cell}
          handlers={handlers}
          onSetState={handleSetState}
          execComputations={cell.execComputations}
          readOnly={formInputProps.readOnly ?? cell.readOnly ?? readOnly}
          modelPath={formInputProps.modelPath || modelPath}
        />
      )
    }
  }
  if (!cellComponent && cell.type) {
    cellComponent = (
      <Col key={uuidv4()} xs={12}>
        <ContentComponent
          debugInfo={cell}
          handlers={handlers}
          onSetState={handleSetState}
          entity={entity}
          noCard={true}
          content={cell}
          modelPath={modelPath}
          {...cardProps}
        />
      </Col>
    )
  }
  return cellComponent
}

let navigationRef
export function setNavigationRef(ref) {
  navigationRef = ref
}

export function getNavigationRef() {
  return navigationRef
}

function addCheckConfigNotifications({ settingsType, inserts = [], updates = [], deletes = [], userUpdated = [] }) {
  const messageMaxLength = 200
  const nbOfInserts = inserts.length
  const nbOfUpdates = updates.length
  const nbOfUserUpdates = userUpdated.length
  const nbOfDeletes = deletes.length

  if (!nbOfInserts && !nbOfUpdates && !nbOfDeletes && !nbOfUserUpdates) return

  const options = { autoDismiss: 15, level: "info" }
  if (nbOfInserts) {
    let insertsString = inserts.join(", ")
    if (insertsString.length > messageMaxLength) insertsString.substring(0, messageMaxLength) + " [...]"
    options.message = `Added ${nbOfInserts} ${settingsType}: ${insertsString}`
    addNotification(options)
  }
  if (nbOfUpdates) {
    let updatesString = updates.join(", ")
    if (updatesString.length > messageMaxLength) updatesString.substring(0, messageMaxLength) + " [...]"
    options.message = `Updated ${nbOfUpdates} ${settingsType}: ${updatesString}`
    addNotification(options)
  }
  if (nbOfUserUpdates) {
    let updatesString = userUpdated.join(", ")
    if (updatesString.length > messageMaxLength) updatesString.substring(0, messageMaxLength) + " [...]"
    options.message = `Updated ${nbOfUserUpdates} user ${settingsType}: ${updatesString}`
    addNotification(options)
  }
  if (nbOfDeletes) {
    let deletesString = deletes.join(", ")
    if (deletesString.length > messageMaxLength) deletesString.substring(0, messageMaxLength) + " [...]"
    options.message = `Deleted ${nbOfDeletes} ${settingsType}: ${deletesString}`
    addNotification(options)
  }
}

let isCheckingTenantConfig = false
export async function checkTenantConfig({ allTenants, softReload = true, tenant } = {}) {
  if (!canCheckTenantConfig() || isCheckingTenantConfig) return

  try {
    isCheckingTenantConfig = true
    const {
      errors = [],
      insertedLists = [],
      updatedLists = [],
      deletedLists = [],
      insertedScripts = [],
      updatedScripts = [],
      deletedScripts = [],
      insertedReports = [],
      updatedReports = [],
      deletedReports = [],
      userLists = [],
      userScripts = [],
      lastConfigCheckDate,
    } = (await axios.get("/api/core/tenants/check-config" + objectToSearchParam({ allTenants, tenant }))).data || {}

    for (const error of errors) addOops(error)

    const touchedLists = [...insertedLists, ...updatedLists, ...deletedLists]
    const touchedScripts = [...insertedScripts, ...updatedScripts, ...deletedScripts]
    const touchedReports = [...insertedReports, ...updatedReports, ...deletedReports]
    const userUpdatedLists = []
    const userUpdatedScripts = []

    const lastLoadingDate = getLoadingDate()
    if (userLists.length) {
      for (let i = 0; i < userLists.length; i++) {
        const { _updateDate, name } = userLists[i]
        if (_updateDate >= lastLoadingDate && !touchedLists.find(touchedList => touchedList === name)) {
          touchedLists.push(name)
          userUpdatedLists.push(name)
        }
      }
    }
    if (userScripts.length) {
      for (let i = 0; i < userScripts.length; i++) {
        const { _updateDate, name } = userScripts[i]
        if (_updateDate >= lastLoadingDate && !touchedScripts.find(touchedScript => touchedScript === name)) {
          touchedScripts.push(name)
          userUpdatedScripts.push(name)
        }
      }
    }

    addCheckConfigNotifications({
      settingsType: "lists",
      inserts: insertedLists,
      updates: updatedLists,
      deletes: deletedLists,
      userUpdated: userUpdatedLists,
    })
    addCheckConfigNotifications({
      settingsType: "scripts",
      inserts: insertedScripts,
      updates: updatedScripts,
      deletes: deletedScripts,
      userUpdated: userUpdatedScripts,
    })
    addCheckConfigNotifications({ settingsType: "reports", inserts: insertedReports, updates: updatedReports, deletes: deletedReports })

    if (!allTenants && !tenant) {
      const nbOfTouchedLists = touchedLists.length
      const nbOfTouchedScripts = touchedScripts.length
      const nbOfTouchedReports = touchedReports.length

      const isConfigStale = lastConfigCheckDate && lastConfigCheckDate > lastLoadingDate
      if (nbOfTouchedLists > 0 || nbOfTouchedScripts > 0 || isConfigStale) {
        if (isConfigStale && !nbOfTouchedLists && !nbOfTouchedScripts) {
          addNotification({ message: "Configuration is stale", level: "warning" })
        }

        resetLists({ itemsToReset: updatedLists })

        // Reset all scripts cache instead of just the ones updated.
        // This is necessary because we can request a typed entity layout which may return the standard entity layout
        // and store it in cache under the typed entity layout script name.
        // Consequently, if the standard entity layout script is flagged as updated in the check config response,
        // the cache for the typed entity layout is not cleared, requiring a manual refresh to update the cache
        resetScripts()

        resetCache()

        if (touchedScripts.includes(fullStackScripts.userConfiguration) || isConfigStale) {
          // give some time for the server to refresh its cache
          await sleep(1500)
          await loadUserConfiguration()
          await applyUserConfigurationStyles({ implementation: getTenant() })
        }

        if (touchedScripts.includes("string-overrides")) {
          // give some time for the server to refresh its cache
          await sleep(1500)
          localizationService.resetLocale({ toCurrentLocale: true })
        }

        if (softReload) {
          softReloadRoute()
        }
      } else if (!nbOfTouchedReports) {
        addNotification({ message: "Configuration is up to date" })
      }
    }
  } catch (error) {
    addOops(error)
  } finally {
    isCheckingTenantConfig = false
  }
}

/**
 * Simply calling new Date() or toLocaleString or even toString()
 * can introduce characters that depends on the caller OS and that
 * are not supported in some places like HTTP headers.
 * Use this function to ensure that the date is kept with the timezone while
 * being compatible in all cases.
 */
export function getDateStringWithOffset(date) {
  const currentDate = date || new Date()
  const isoDateString = currentDate.toISOString()

  // Extract the date and time part (without milliseconds and Z)
  const dateAndTime = isoDateString.slice(0, 19)

  // Extract the timezone offset in the format ±HH:mm
  const timezoneOffset = currentDate.getTimezoneOffset()
  const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60)
  const offsetMinutes = Math.abs(timezoneOffset) % 60
  const offsetString =
    (timezoneOffset >= 0 ? "-" : "+") + (offsetHours < 10 ? "0" : "") + offsetHours + ":" + (offsetMinutes < 10 ? "0" : "") + offsetMinutes

  return dateAndTime + offsetString
}

export function isNewTabOrWindowClick(event) {
  const isMac = window.navigator.userAgentData?.platform === "macOS" || window.navigator.platform?.match("Mac")
  return (isMac ? event.metaKey : event.ctrlKey) || event.shiftKey || event.button === 1
}

/**
 * Examples for setting image dimensions:
 *  ![my pic](my_pic.png=300x)
 *  ![my pic](my_pic.png=300x400)
 *  ![my pic](my_pic.png=x400)
 *
 * Syntax is: =WidthxHeight
 */
function getMarkdownImageOptions({ href, title, altText }) {
  let hrefParts = href.split("=")
  if (hrefParts.length === 1) hrefParts = href.split(";")
  if (hrefParts.length === 1) hrefParts = href.split(",")

  let imgHeight = ""
  let imgWidth = ""
  if (hrefParts[1]) {
    const size = hrefParts[1].split("x")
    imgHeight = `height="${size[1]}"`
    imgWidth = `width="${size[0]}"`
  }

  const imgSrc = hrefParts[0]
  return {
    imgSrc,
    imgHeight,
    imgWidth,
    tag: `<img ${altText ? `alt="${altText}"` : ""} title="${title || altText}" ${imgHeight} ${imgWidth} src="${imgSrc}" />`,
  }
}

addHook("beforeSanitizeAttributes", function (node) {
  if ("target" in node) {
    node.setAttribute("data-target", node.getAttribute("target"))
  }
})

addHook("afterSanitizeAttributes", function (node) {
  if ("target" in node) {
    const target = node.getAttribute("data-target")
    if (target === "_blank") {
      // https://developer.chrome.com/docs/lighthouse/best-practices/external-anchors-use-rel-noopener/
      node.setAttribute("target", target)
      node.setAttribute("rel", "noopener noreferrer")
    }
    node.removeAttribute("data-target")
  }
})

// highlight.js is large, so it is no longer statically imported (which would bundle it into the
// main chunk via this app-wide module). It is loaded on demand the first time a fenced code block
// is rendered. Until it has loaded, code blocks are rendered as escaped plain text and upgraded in
// place once the library is available. After the first load, highlighting happens synchronously,
// so the rendered output is identical to the previous static-import behaviour.
let hljs = null
let hljsLoadingPromise = null

function loadHljs() {
  if (hljs) return Promise.resolve(hljs)
  if (!hljsLoadingPromise) {
    hljsLoadingPromise = import("highlight.js")
      .then(module => {
        hljs = module.default
        highlightPendingCodeBlocks()
        return hljs
      })
      .catch(error => {
        console.log(error)
        hljsLoadingPromise = null
      })
  }
  return hljsLoadingPromise
}

function escapeHtml(value = "") {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}

// Turns bare http(s) URLs found in already HTML-escaped/rendered code into clickable anchors.
// The default marked renderer outputs code content as plain text, so links inside code blocks
// would otherwise not be clickable. The regex stops at "<", so highlight.js span markup is left intact.
function linkifyUrlsInHtml(html = "") {
  return String(html).replace(/https?:\/\/[^\s<>"'`]+/g, url => {
    // Keep trailing punctuation out of the link.
    const trailing = url.match(/[).,;:!?]+$/)?.[0] || ""
    const href = trailing ? url.slice(0, url.length - trailing.length) : url
    return `<a href="${href}">${href}</a>${trailing}`
  })
}

// Upgrades any code blocks that were rendered before highlight.js finished loading.
// Once hljs is loaded the code renderer highlights synchronously, so no pending blocks remain.
function highlightPendingCodeBlocks() {
  if (!hljs || typeof document === "undefined") return
  const codeBlocks = document.querySelectorAll("pre code[data-hljs-pending]")
  codeBlocks.forEach(codeBlock => {
    const lang = codeBlock.getAttribute("data-hljs-lang")
    const language = hljs.getLanguage(lang) ? lang : "plaintext"
    codeBlock.removeAttribute("data-hljs-pending")
    codeBlock.removeAttribute("data-hljs-lang")
    codeBlock.className = `hljs language-${language}`
    hljs.highlightElement(codeBlock)
  })
}

function getMarkedRendererOptions({ toc } = {}) {
  const documentationRoute = "/documentation"
  const legacyApiDocRoot = "/api/doc/"

  const linkRenderer = options => {
    const { href, title, text: optionsText, tokens } = options
    let text = optionsText
    // Without this check marked throws an infinite loop error when parsing text containing raw links
    if (tokens?.[0]?.raw !== tokens?.[0]?.text) {
      text = marked.parseInline(optionsText)
    }

    // When we want to display bare URL markdown linters forces us to
    // repeat the link both in the text and href parts [some-link](some-link).
    // To avoid that we support [](some-link) and the text part automatically gets the href.
    if (!text && href) text = href

    let optionalTags = ""
    let hasNewTabOption
    if (title) {
      // We only support target=_blank, no need to support other values as they are rare
      const strippedTitle = title.replaceAll("[newTab]", () => {
        hasNewTabOption = true
        return ""
      })
      if (strippedTitle) optionalTags += ` title="${strippedTitle}"`
      if (hasNewTabOption) {
        if (optionalTags) optionalTags += " "
        optionalTags += ` target="_blank"`
      }
    }

    const { pathname } = window.location
    if (pathname.startsWith(`${documentationRoute}/`) || pathname === documentationRoute) {
      const hashIndex = window.location.href.indexOf("#")

      return href.startsWith("http")
        ? `<a${optionalTags} href="${href}">${text}</a>`
        : href.startsWith("#")
          ? `<a${optionalTags} href="${(hashIndex !== -1 ? window.location.href.substring(0, hashIndex) : window.location.href) + href}">${text}</a>`
          : href.startsWith(".")
            ? `<a${optionalTags} href="${window.location.pathname + "/../" + href}">${text}</a>`
            : href.startsWith("/")
              ? `<a${optionalTags} href="${hasNewTabOption ? window.location.origin : ""}${href}">${text}</a>`
              : href.startsWith("mailto") || href.startsWith("tel")
                ? `<a${optionalTags} href="${href}">${text}</a>`
                : `<a${optionalTags} href="${`${documentationRoute}/${href.replace(
                    ".md",
                    "",
                  )}`}" data-original-href="${`${documentationRoute}/${href}`}">${text}</a>`
    }

    return `<a${optionalTags} href="${href}">${text}</a>`
  }

  // Renders the content of an inline code span (or code block) into safe HTML while keeping links
  // clickable: markdown link syntax [text](href) is honored (routed through linkRenderer so that
  // relative, http, mailto... hrefs are all handled), and remaining bare http(s) URLs are linkified.
  // Markdown does not parse links inside code, so without this a `[label](/path)` stays plain text.
  const renderCodeContentWithLinks = codeText => {
    const markdownLinkRegExp = /\[([^\]]*)\]\(([^)\s]+)\)/g
    let result = ""
    let lastIndex = 0
    let match
    while ((match = markdownLinkRegExp.exec(codeText)) !== null) {
      const [raw, label, href] = match
      result += linkifyUrlsInHtml(escapeHtml(codeText.slice(lastIndex, match.index)))
      result += linkRenderer({ href, text: escapeHtml(label || href) })
      lastIndex = match.index + raw.length
    }
    result += linkifyUrlsInHtml(escapeHtml(codeText.slice(lastIndex)))
    return result
  }

  return {
    renderer: {
      link: linkRenderer,
      table(options) {
        const { header, rows } = options
        const headerText = header
          ?.map(th => {
            return `<th>${marked.parseInline(th.text)}</th>`
          })
          .join("")

        const tableBody = rows
          .map(tr => {
            const trText = tr
              .map(td => {
                let tdText = td.text || ""
                for (const token of td.tokens || []) {
                  if (token.type === "codespan") {
                    tdText = tdText.replaceAll(token.raw, `<code class='break-normal'>${token.text}</code>`)
                  } else if (token.type === "link") {
                    tdText = linkRenderer(token)
                  } else {
                    tdText = marked.parseInline(tdText)
                  }
                }
                return `<td>${tdText}</td>`
              })
              .join("")
            return `<tr>${trText}</tr>`
          })
          .join("")

        return `<table class="table"><thead>${headerText}</thead><tbody>${tableBody}</tbody></table>`
      },
      codespan(options) {
        // The default renderer outputs the escaped text as-is, so links in an inline code span
        // stay plain text. We honor markdown link syntax and bare URLs to keep them clickable.
        return `<code>${renderCodeContentWithLinks(options.text)}</code>`
      },
      code(options) {
        const { text, lang } = options
        if (lang === "mermaid") {
          return `<div id="mermaid-${uuidv4()}">${text}</div>`
        }
        if (hljs) {
          const language = hljs.getLanguage(lang) ? lang : "plaintext"
          return `<pre><code>${hljs.highlight(text, { language }).value}</code></pre>`
        }
        // highlight.js is not loaded yet: render escaped code now and upgrade it in place once the library loads.
        loadHljs()
        return `<pre><code data-hljs-pending data-hljs-lang="${escapeHtml(lang || "")}">${escapeHtml(text)}</code></pre>`
      },
      image(options) {
        const { href, title, text = href } = options
        let { tag, imgSrc, imgHeight, imgWidth } = getMarkdownImageOptions({ href, title, altText: text })

        const { pathname } = window.location
        if (pathname.startsWith(`${documentationRoute}/`) || pathname === documentationRoute) {
          const docPath = window.location.pathname.replace(documentationRoute, "")
          const docFolder = docPath.substr(0, docPath.lastIndexOf("/") + 1)
          imgSrc = window.location.origin + legacyApiDocRoot + "integration" + docFolder + imgSrc
          const src = (href.startsWith("http") || href.startsWith("/") ? href : imgSrc) + `?token=${getAuthorizationToken()}`

          return `<img ${text ? `alt="${text}"` : ""} title="${title || text}" ${imgHeight} ${imgWidth} src="${src}" />`
        }

        return tag
      },
      heading(options) {
        const { depth: level, text: optionsText, tokens } = options
        const { text: firstTokenText } = tokens?.[0] || {}
        const firstText = firstTokenText || optionsText
        const id = firstText
          ?.toLowerCase()
          .replaceAll(" ", "-")
          .replace(/[():;.,`—"'\/\?]/g, "")

        const headingText = marked.parseInline(optionsText)
        if (Array.isArray(toc)) {
          toc.push({ id, level, text: headingText })
        }
        return `<h${level} id="${id}">${headingText}</h${level}>`
      },
      text(options) {
        const { raw, tokens } = options
        // When there are sub-tokens (bold, link, codespan...) we still need to render them,
        // but we can't just bail out to the default renderer as it drops the \n -> <br> conversion below.
        const html = tokens?.length ? this.parser.parseInline(tokens) : raw
        // Note: this renderer runs once per text fragment (the plain runs between bold/code/link spans
        // within a paragraph), not once for the whole paragraph. So we must not strip a trailing \n here:
        // it's often a mid-paragraph line break (e.g. right before the next **bold** span), not the last
        // line of the paragraph.
        return html.replace(/(\r\n|\r|\n)/g, "<br>")
      },
      listitem(options) {
        const { checked, raw, tokens } = options

        // checked can be undefined
        // It is set to true or false when users type - [x] or - [ ]
        let checkboxInput = ""
        if (checked === true) {
          checkboxInput = `<i class="icn-xs icn-checkbox-checked"></i> `
        }
        if (checked === false) {
          checkboxInput = `<i class="icn-xs icn-checkbox"></i> `
        }

        const rawRestNumber = raw.match(/^\s*(\d+)[.)]/)?.[1] ?? ""
        // We need this to continue parsing the list item content, otherwise raw text is displayed.
        const parsedTokens = this.parser.parse(tokens)
        // The end </p> tag is not always at the end due to carriage returns.
        const cleanedTokens = parsedTokens.replace(/^\s*<p>/, "").replace(/<\/p>/, "")
        // The default behavior of the HTML language is to number ordered list sequentially.
        // In markedjs the given numbers are ignored, they are just used to detect that the list is ordered or not.
        // To keep the given numbers, we must manually pass them in the `value` attribute.
        return `<li${rawRestNumber && !isNaN(rawRestNumber) ? ` value="${rawRestNumber}"` : ""}>${checkboxInput}${cleanedTokens}</li>`
      },
    },
  }
}

export function getMermaidIds(htmlString) {
  const mermaidIds = Array.from(htmlString.matchAll(/id="(mermaid-[^"]+)"/g).map(m => `#${m[1]}`))
  return mermaidIds
}

/**
 * Scrolls to the rendered markdown heading (h1-h5) whose id matches `headerId`.
 * Shared by DocumentationPage and ContentComponent so header anchoring behaves identically.
 * Returns true when a matching heading was found.
 */
export function scrollToHeaderById(headerId) {
  if (!headerId) return false
  const headers = [
    ...document.getElementsByTagName("h1"),
    ...document.getElementsByTagName("h2"),
    ...document.getElementsByTagName("h3"),
    ...document.getElementsByTagName("h4"),
    ...document.getElementsByTagName("h5"),
  ]
  for (const header of headers) {
    if (header.id === headerId) {
      header.scrollIntoView({ behavior: "smooth" })
      return true
    }
  }
  return false
}

// Resets the markdown scroll position to the top after navigating to another document.
// The effective scroll container depends on the layout: in the full DocumentationPage the
// fixed-height `.documentation-content` scrolls (because `overflow-x: auto` forces `overflow-y`
// to `auto` on it), while an embedded markdown ContentComponent may let an outer element scroll.
// Body/documentElement never scroll in the app's default (data-ucsl) mode, so we walk up from the
// content element and reset every scrollable ancestor to cover all cases.
export function scrollDocumentationContentToTop(contentElement) {
  let element = contentElement || document.querySelector(".documentation-content")
  while (element) {
    if (element.scrollTop) element.scrollTop = 0
    element = element.parentElement
  }
}

export async function execMermaid({ mermaidIds = [] } = {}) {
  if (!mermaidIds?.length) return
  try {
    // Loaded on demand so the (large) `mermaid` package stays out of the main bundle.
    // execMermaid is always called fire-and-forget after markdown is rendered to the DOM.
    const { default: mermaid } = await import("mermaid")
    await mermaid.run({ querySelector: mermaidIds.join(",") })
  } catch (error) {
    console.log(error)
  }
}

/**
 * Take good note of this doc https://marked.js.org/using_pro#use
 * When calling use(), if we return the value "false" then the previous override in the load chain is used.
 * See this link for the extensions options https://github.com/markedjs/marked/blob/master/src/Tokens.ts
 */
export function setMarkedRendererOptions() {
  marked.use(getMarkedRendererOptions())
}

// Rough check for KaTeX delimiters ($...$, $$...$$, \(...\), \[...\]) so we skip the (lazy)
// KaTeX pass — and never load its chunk — for the many markdown renders that contain no math.
const latexDelimiterRegExp = /\$\$[\s\S]+?\$\$|(?<!\\)\$[^\s$][^$]*?\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/

export function hasLatex(text) {
  return typeof text === "string" && latexDelimiterRegExp.test(text)
}

/**
 * Renders LaTeX in an already-rendered markdown DOM element using KaTeX's own auto-render
 * pass — the same post-render, fire-and-forget approach as execMermaid. KaTeX and its CSS
 * are loaded on demand (into a single `katex` chunk) and only when the markdown actually
 * contains math, so nothing katex-related sits in the main bundle. Because it operates on
 * the DOM (not on `marked`), it works for every markdown surface regardless of which marked
 * instance produced it, and needs no re-render — the math upgrades in place after paint.
 *
 * Scope is always the passed `element` only, never the document, so plain `$` amounts
 * elsewhere on the page are never mistaken for math.
 */
export async function execKatex({ element, html } = {}) {
  if (!element) return
  // Bail (and never load the chunk) when there is no math to render.
  if (html !== null && !hasLatex(html)) return
  try {
    const [{ default: renderMathInElement }] = await Promise.all([
      import(/* webpackChunkName: "katex" */ "katex/contrib/auto-render"),
      import(/* webpackChunkName: "katex" */ "katex/dist/katex.min.css"),
    ])
    renderMathInElement(element, {
      throwOnError: false,
      delimiters: [
        { left: "$$", right: "$$", display: true },
        { left: "$", right: "$", display: false },
        { left: "\\(", right: "\\)", display: false },
        { left: "\\[", right: "\\]", display: true },
      ],
    })
  } catch (error) {
    console.log(error)
  }
}

/**
 * We can't pass the documents directly because new images are inserted in them after the renderer is set.
 */
function getMarkedRendererImage({ entityRegistration, entityName, getDocumentsFn }) {
  const rootUrl = getEntityServerRoute(entityName) + "/" + entityRegistration
  return {
    renderer: {
      image({ href, title, text }) {
        let { imgSrc, imgHeight, imgWidth } = getMarkdownImageOptions({ href, title, altText: text })

        const documents = getDocumentsFn()
        if (documents && !imgSrc.startsWith("http")) {
          const imageId = documents?.find(it => it.name === imgSrc)?._id
          if (imageId) {
            const docImgSrc = `${rootUrl}/documents/${imageId}/content?token=${getAuthorizationToken()}`
            return `<img ${text ? `alt="${text}"` : ""} ${imgHeight} ${imgWidth} src="${docImgSrc}" />`
          }
        }

        // very important to return false
        // read doc on function getMarkedRendererOptions
        return false
      },
    },
  }
}

export function getMarkedInstance({ toc, entityName, entityRegistration, getDocumentsFn } = {}) {
  const markedInstance = new Marked()
  markedInstance.use(getMarkedRendererOptions({ toc }))
  if (getDocumentsFn) {
    markedInstance.use(getMarkedRendererImage({ entityName, entityRegistration, getDocumentsFn }))
  }
  return markedInstance
}

export function formatCurrency(number, locale, options = {}) {
  let _options = { currencyDisplay: getConfigAtPath("currencyDisplay") }
  if (isNumber(options)) {
    _options.minimumFractionDigits = options
    _options.maximumFractionDigits = options
  } else if (typeof options === "string") {
    _options.currency = options
  } else if (isObject(options)) {
    _options = { ..._options, ...options }
  }

  return _formatCurrency(number, locale, _options)
}

export function formatDate(date, locale, option) {
  return _formatDate(date, locale, { timeZone: getTimeZone(), ...(option || {}) })
}

export function formatDateTime(date, locale, option) {
  return _formatDateTime(date, locale, { timeZone: getTimeZone(), ...(option || {}) })
}

export function dateNoTime(dateOrYear, month, day) {
  return getConfigAtPath("useDateNoTime")
    ? _dateNoTime(dateOrYear, month, day)
    : !(dateOrYear instanceof Date) && month && day
      ? new Date(dateOrYear, month, day)
      : dateOrYear
}

export function dateAdd(date, period) {
  return _dateAdd(date, period, getConfigAtPath("useDateNoTime"))
}

export function parseFrequencyValue(frequency, { usePlural } = {}) {
  const result = _parseFrequencyValue(frequency, { usePlural })
  return { ...result, label: localizationService.loc(result.label) }
}

/**
 * This function is important for web accessibility.
 */
export function handleAccessibleOnKeyDown({ event, history, fn, linkTo }) {
  if (event.key === " " || event.key === "Enter") {
    event.preventDefault()
    event.stopPropagation()

    if (linkTo) {
      history.push(linkTo)
      if (fn) fn()
    } else if (fn) {
      fn()
    }
  }
}

export function onDropdownToggleKeyDown(event) {
  const isBottomArrowKey = event.keyCode === 40
  if (isBottomArrowKey) {
    const { target } = event
    target.nextSibling?.children?.[0].focus()
  }
}

export function onDropdownMenuButtonKeyDown(event) {
  const isTopArrowKey = event.keyCode === 38
  const isBottomArrowKey = event.keyCode === 40
  if (isBottomArrowKey) {
    const { target } = event
    target.nextSibling?.focus()
  } else if (isTopArrowKey) {
    const { target } = event
    target.previousSibling?.focus()
  }
}

export function adjustAmount({ pr, amount, payableAmountsDisplay }) {
  if (isNaN(amount)) return amount
  if (payableAmountsDisplay === "absolute") return amount
  return pr === "P" ? -amount : amount // By default we append a negative sign for "payable" amounts (to keep backward compatibility)
}

export function getPhoneNumberInfo(phone = "", countryOptions = []) {
  const sortedCountryOptions = [...countryOptions].sort((a, b) => b.value?.length - a.value?.length)

  const country = sortedCountryOptions.find(country => phone.startsWith(country.value))

  if (country) {
    const callingCode = country
    const localNumber = phone.replace(callingCode.value, "") || ""

    return { callingCode, localNumber: localNumber, countryCode: country.value2 }
  } else {
    return { callingCode: "", localNumber: phone || "" }
  }
}

export function handleRedirect({ redirectTo, location, history, obj }) {
  if (!redirectTo) return

  const isSamePage = location.pathname === redirectTo
  if (isSamePage) {
    softReloadRoute()
    return
  }

  if (typeof redirectTo === "string") {
    const url = replaceVariables(redirectTo, obj)
    if (url.startsWith("http")) window.location.href = url
    else history.push(url)
    return
  }

  if (typeof redirectTo === "object" && redirectTo.url) {
    redirectTo.url = replaceVariables(redirectTo.url, obj)
    window.open(redirectTo.url, redirectTo.target, redirectTo.windowFeatures)
  }
}

export function checkOrganizationNameInclude(columns, include) {
  const isOrganizationNameColumnDisplayed = columns.find(it => it.name === "organization.name" && !it.hidden)
  if (!isOrganizationNameColumnDisplayed) return include

  if (include === undefined) include = []
  else if (typeof include === "string") include = include.split(",")
  if (!Array.isArray(include)) return include

  if (!include.includes("organizationName")) include.push("organizationName")
  return include.join(",")
}

export function getHttpMethodBgColor(method) {
  const methodColor =
    method === "GET"
      ? "green"
      : method === "POST"
        ? "orange"
        : method === "PATCH"
          ? "purple"
          : method === "PUT"
            ? "blue"
            : method === "DELETE"
              ? "red"
              : method === "OPTIONS"
                ? "gray-darkest"
                : "black"
  return `var(--color-${methodColor})`
}

export function adjustForDisplay(message, maxLength = 60) {
  if (!message) return message
  const splits = message
    .split(" ")
    .map(it => it + " ")
    .map(it => it.split("\n").map(it => it + "\n"))
    .flat()
  for (let i = 0; i < splits.length; i++) {
    const split = splits[i]
    if (split.length > maxLength) {
      splits[i] = split.substring(0, maxLength)
      splits[i] += "..."
      if (split.trimEnd().at(-1) === '"') splits[i] += '"'
    }
  }
  return splits.join(" ")
}

export function cropText(text, maxLength = 150) {
  if (!text) return text
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
}

export function computeWorkflowStatus({ workflow, currentStatus, successStatus, errorStatus, warningStatus, ongoingStatus, stoppedStatus }) {
  if (workflow.status) return

  const rootStyles = getComputedStyle(document.documentElement)
  const infoBgColor = rootStyles.getPropertyValue("--info-states-color").trim()
  const successBgColor = rootStyles.getPropertyValue("--success-states-color").trim()
  const dangerBgColor = rootStyles.getPropertyValue("--danger-states-color").trim()
  const orangeBgColor = rootStyles.getPropertyValue("--btn-orange-states-color").trim()
  const purpleBgColor = rootStyles.getPropertyValue("--btn-purple-states-color").trim()
  const grayBgColor = rootStyles.getPropertyValue("--btn-gray-states-color").trim()
  const blackBgColor = rootStyles.getPropertyValue("--color-black").trim()

  workflow.status = []
  if (!workflow.transitions) return

  workflow.transitions.forEach(transition => {
    const sources = Array.isArray(transition.source) ? transition.source : [transition.source]
    const targets = Array.isArray(transition.target) ? transition.target : [transition.target]
    const sourcesAndTargets = [...sources, ...targets]

    for (const item of sourcesAndTargets) {
      if (workflow.status.find(({ code }) => code === item)) continue

      const status = { code: item, backgroundColor: grayBgColor, color: "white" }
      // Order matters here, we want to first show errors, then ongoing, then current and lastly success status.
      if (errorStatus?.includes(item)) {
        status.backgroundColor = dangerBgColor
      } else if (warningStatus?.includes(item)) {
        status.backgroundColor = orangeBgColor
      } else if (stoppedStatus?.includes(item)) {
        status.backgroundColor = blackBgColor
      } else if (ongoingStatus?.includes(item)) {
        status.backgroundColor = purpleBgColor
      } else if (item === currentStatus) {
        status.backgroundColor = infoBgColor
      } else if (successStatus?.includes(item)) {
        status.backgroundColor = successBgColor
      } else {
        delete status.color
      }
      if (item === currentStatus) {
        status.borderColor = infoBgColor
      }

      workflow.status.push(status)
    }
  })
}

export function getWatermark(fileExtension, documentType, event) {
  if (documentType?.watermark?.event === event) {
    let SUPPORTED_FORMATS_FOR_WATERMARK = [...SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION, "pdf"]
    if (documentType?.convertToPdf?.event === event)
      SUPPORTED_FORMATS_FOR_WATERMARK = [...SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION, ...SUPPORTED_FORMATS_FOR_WATERMARK]
    if (!SUPPORTED_FORMATS_FOR_WATERMARK.includes(fileExtension)) {
      // Error message kanged from FormInput.jsx:validateUploadFiles
      addOops(`${loc("Forbidden document extension")} "${fileExtension}". ${loc("Accepted formats")}: ${SUPPORTED_FORMATS_FOR_WATERMARK.join(", ")}`)
      return "error"
    }
    return JSON.stringify({ ...documentType.watermark, event: undefined })
  }
}

export async function getFileOrWatermark(file, documentType, event) {
  if (documentType?.watermark?.event === event) {
    const filename = file.name
    const fileExtension = filename?.split(".")?.pop()?.toLowerCase()
    let SUPPORTED_FORMATS_FOR_WATERMARK = [...SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION, "pdf"]
    if (documentType?.convertToPdf?.event === event)
      SUPPORTED_FORMATS_FOR_WATERMARK = [...SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION, ...SUPPORTED_FORMATS_FOR_WATERMARK]
    if (!SUPPORTED_FORMATS_FOR_WATERMARK.includes(fileExtension)) {
      // Error message kanged from FormInput.jsx:validateUploadFiles
      addOops(`${loc("Forbidden document extension")} "${fileExtension}". ${loc("Accepted formats")}: ${SUPPORTED_FORMATS_FOR_WATERMARK.join(", ")}`)
      return "error"
    }
    // TODO : temporary, it is the API which must add the watermark to the image, as for the pdf
    if (documentType?.convertToPdf?.event !== event && SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION.includes(fileExtension)) {
      return await addTextWatermarkToImage(file, { ...documentType.watermark, event: undefined })
    } else {
      return JSON.stringify({ ...documentType.watermark, event: undefined })
    }
  }
}

export function getConvertToPdf(fileExtension, documentType, event) {
  if (fileExtension === "pdf") return
  if (documentType?.convertToPdf?.event === event) {
    const SUPPORTED_FORMATS_FOR_PDF_CONVERSION = [...SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION, ...SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION]
    if (!SUPPORTED_FORMATS_FOR_PDF_CONVERSION.includes(fileExtension)) {
      // Error message kanged from FormInput.jsx:validateUploadFiles
      addOops(
        `${loc("Forbidden document extension")} "${fileExtension}". ${loc("Accepted formats")}: ${SUPPORTED_FORMATS_FOR_PDF_CONVERSION.join(", ")}`,
      )
      return "error"
    }
    return true
  }
}

export async function addTextWatermarkToImage(file, watermark) {
  let _watermark = {
    degrees: -45,
    font: "Helvetica",
    fontSize: 18,
    color: "rgb(255, 255, 255)",
    opacity: 0.7,
    letterSpacing: 2,
    wordSpacing: 0,
    watermarkSpacing: 100,
    lineSpacing: 200,
  }
  if (typeof watermark === "object") {
    _watermark = { ..._watermark, ...watermark }
  } else if (typeof watermark === "string") {
    _watermark = { ..._watermark, text: watermark }
  }

  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")

        const canvasWidth = img.width
        const canvasHeight = img.height
        canvas.width = canvasWidth
        canvas.height = canvasHeight
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)

        const rad = (_watermark.degrees * Math.PI) / 180
        ctx.rotate(rad)
        ctx.font = _watermark.fontSize + "px " + _watermark.font
        ctx.fillStyle = _watermark.color
        ctx.letterSpacing = _watermark.letterSpacing + "px"
        ctx.wordSpacing = _watermark.wordSpacing + "px"
        ctx.textAlign = "left"
        ctx.globalAlpha = _watermark.opacity
        const text = loc(_watermark.text)
        const metrics = ctx.measureText(text)
        const watermarkTextWidth = metrics.width + _watermark.watermarkSpacing
        const watermarkTextHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
        const columns = canvasWidth / watermarkTextWidth
        const lines = canvasHeight / (watermarkTextHeight + _watermark.lineSpacing)
        // -10 instead of 0 (and * 2) because the rotation angle shifts the watermark upwards
        for (let l = -10; l < lines * 2; l++) {
          // -10 instead of 0 because the rotation angle shifts the watermark to the right
          for (let i = -10; i < columns; i++) {
            ctx.fillText(text, i * watermarkTextWidth, l * (watermarkTextHeight + _watermark.lineSpacing) + watermarkTextHeight)
          }
        }

        canvas.toBlob(
          blob => {
            if (blob) resolve(blob)
          },
          "image/jpeg",
          0.85,
        )
      }
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  })
}

export function parseMaxDocumentSize(maxDocumentSize) {
  if (!maxDocumentSize) return
  if (typeof maxDocumentSize === "number") return maxDocumentSize

  // "2MB" => 2_097_152
  if (typeof maxDocumentSize === "string") {
    maxDocumentSize = maxDocumentSize.toUpperCase()

    if (maxDocumentSize.endsWith("MB")) maxDocumentSize = 1024 * 1024 * Number(maxDocumentSize.replace("MB", ""))
    else maxDocumentSize = Number(maxDocumentSize)
  }

  if (typeof maxDocumentSize === "number" && maxDocumentSize) return maxDocumentSize
}

/**
 * We could identify all fields that have props_ and emit an errors object with them only.
 * But this is cumbersome and prone to misses, so we ask all of them to update, like it was before the form input optimization.
 * This is ok because we debounce the event and pages or modals have rarely a lot of form inputs rendered at a time.
 */
export const emitDebouncedFormInputForceComponentUpdate = debounce(({ errors, reloadLists, force = true } = {}) => {
  window.dispatchEvent(new CustomEvent(customEvents.formInput.forceComponentUpdate, { detail: { errors, reloadLists, force } }))
}, internalDebounce)

export function softReloadRoute({ event, behavior } = {}) {
  event?.preventDefault()

  const reloadButtons = behavior === "tryReloadData" ? document.getElementsByClassName(reloadDataBtnClassName) : []
  if (reloadButtons.length) {
    for (const reloadButton of reloadButtons) {
      reloadButton.click()
    }
  } else {
    window.dispatchEvent(new CustomEvent(customEvents.pagesLayout.reloadRoute))
  }
}

let windowUuid
let windowIdInterval = null

export function getWindowUuid() {
  return windowUuid
}

export function setWindowId() {
  // Already initialized, prevent multiple intervals
  if (windowIdInterval) return

  windowUuid = uuidv4()

  const windowKeyPrefix = "windowUuid_"
  const windowKey = `${windowKeyPrefix}${windowUuid}`
  localStorage.setItem(windowKey, Date.now())

  windowIdInterval = setInterval(() => {
    localStorage.setItem(windowKey, Date.now())

    const now = Date.now()
    for (const localStorageKey in localStorage) {
      if (localStorageKey.startsWith(windowKeyPrefix)) {
        const last = Number(localStorage.getItem(localStorageKey))
        if (now - last > entityPresenceHeartbeatMs) {
          localStorage.removeItem(localStorageKey)
        }
      }
    }
  }, entityPresenceHeartbeatMs)
}

export const keyboardShortcutActions = {
  softReloadRoute: "softReloadRoute",
  showAllSettings: "showAllSettings",
  save: "save",
  toggleSettings: "toggleSettings",
  executeScript: "executeScript",
  launchSearch: "launchSearch",
  toggleAdvancedSearch: "toggleAdvancedSearch",
  toggleSideViewAiSidebar: "toggleSideViewAiSidebar",
  startNewAiConversation: "startNewAiConversation",
  toggleNavControlsDisplayMode: "toggleNavControlsDisplayMode",
  toggleVirtualKeyboard: "toggleVirtualKeyboard",
  clickOnGlobalSearchBtn: "clickOnGlobalSearchBtn",
  clickOnAiViewToggleBtn: "clickOnAiViewToggleBtn",
  collapseAllCards: "collapseAllCards",
  showEntityModelModal: "showEntityModelModal",
  checkTenantConfig: "checkTenantConfig",
  toggleOfflineMode: "toggleOfflineMode",
  toggleSideView: "toggleSideView",
  toggleDebugMode: "toggleDebugMode",
  toggleNoDecorationMode: "toggleNoDecorationMode",
  goToScriptsPage: "goToScriptsPage",
  openQueryToken: "openQueryToken",
}

export function isKeyboardShortcutFor({ event, action }) {
  const { keyCode, metaKey, ctrlKey, shiftKey: SHIFT, altKey: ALT } = event
  const isMac = window.navigator.platform.match("Mac")
  const CTRL = isMac ? metaKey : ctrlKey
  const ESC = keyCode === 27
  const A = keyCode === 65
  const B = keyCode === 66
  const C = keyCode === 67
  const D = keyCode === 68
  const E = keyCode === 69
  const G = keyCode === 71
  const Q = keyCode === 81
  const M = keyCode === 77
  const R = keyCode === 82
  const S = keyCode === 83
  const T = keyCode === 84
  const U = keyCode === 85
  const X = keyCode === 88
  const Z = keyCode === 90
  const F8 = keyCode === 119
  const F9 = keyCode === 120
  const DOT = keyCode === 190

  let isDefined

  if (CTRL && DOT && action === keyboardShortcutActions.showAllSettings) isDefined = true
  if (CTRL && B && action === keyboardShortcutActions.toggleSettings) isDefined = true
  if (CTRL && D && action === keyboardShortcutActions.toggleDebugMode) isDefined = true
  if (CTRL && C && SHIFT && action === keyboardShortcutActions.toggleOfflineMode) isDefined = true
  if (CTRL && G && action === keyboardShortcutActions.clickOnGlobalSearchBtn) isDefined = true
  if (CTRL && M && action === keyboardShortcutActions.showEntityModelModal) isDefined = true
  if (CTRL && U && action === keyboardShortcutActions.checkTenantConfig) isDefined = true
  if (CTRL && S && action === keyboardShortcutActions.save) isDefined = true
  if (CTRL && S && SHIFT && action === keyboardShortcutActions.goToScriptsPage) isDefined = true
  if (CTRL && X && SHIFT && action === keyboardShortcutActions.openQueryToken) isDefined = true

  if (ALT && A && action === keyboardShortcutActions.clickOnAiViewToggleBtn) isDefined = true
  if (ALT && B && action === keyboardShortcutActions.toggleVirtualKeyboard) isDefined = true
  if (ALT && C && action === keyboardShortcutActions.collapseAllCards) isDefined = true
  if (ALT && E && action === keyboardShortcutActions.startNewAiConversation) isDefined = true
  if (ALT && Q && action === keyboardShortcutActions.toggleAdvancedSearch) isDefined = true
  if (ALT && R && action === keyboardShortcutActions.softReloadRoute) isDefined = true
  if (ALT && S && action === keyboardShortcutActions.launchSearch) isDefined = true
  if (ALT && T && action === keyboardShortcutActions.toggleNavControlsDisplayMode) isDefined = true
  if (((ALT && X) || ESC) && action === keyboardShortcutActions.toggleSideView) isDefined = true
  if (ALT && Z && action === keyboardShortcutActions.toggleSideViewAiSidebar) isDefined = true

  if (F8 && action === keyboardShortcutActions.executeScript) isDefined = true
  if (F9 && action === keyboardShortcutActions.toggleNoDecorationMode) isDefined = true

  if (isDefined) {
    event.preventDefault()
    event.stopPropagation()
    return true
  }
}

// isFixed is an arbitrary value independent from the lib, however the lib gives this value as example.
// In addition, the lib reserves the field isDisabled.
// So isFixed is effectively a reserved keyword so implementations must pay attention.
// Whenever these fields are set in options, then styles can be tuned.
// isDisabled is styled with CSS classes, while isFixed is styled inline because the lib doesn't provide a class for it.
export const selectNativeStyles = {
  multiValueLabel: (base, state) => {
    return state.data.isFixed ? { ...base, backgroundColor: "var(--color-gray-darkest)" } : base
  },
  multiValueRemove: (base, state) => {
    return state.data.isFixed ? { ...base, display: "none" } : base
  },
}

/**
 * Passing an empty array in selectableSelectOptions or enabledSelectOptions has the effect of fixing or disabled all options: this is desired effect.
 * If you don't want to fix or disable all options, pass values in array or use fixedSelectOptions / disabledSelectOptions.
 */
export function applySelectOptions({ options, fixedSelectOptions, selectableSelectOptions, disabledSelectOptions, enabledSelectOptions }) {
  if (fixedSelectOptions || selectableSelectOptions || disabledSelectOptions || enabledSelectOptions) {
    for (const option of options) {
      if (fixedSelectOptions?.includes(option.value) || (selectableSelectOptions && !selectableSelectOptions?.includes(option.value))) {
        option.isFixed = true
      }

      if (disabledSelectOptions?.includes(option.value) || (enabledSelectOptions && !enabledSelectOptions?.includes(option.value))) {
        option.isDisabled = true
      }
    }
  }
}

export function getLeafletProps() {
  return {
    attribution: "&amp;copy <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    referrerPolicy: "origin",
  }
}

/**
 * Toggles audits details and enrich them with more information.
 * This functions mutates the audits.
 * For now the list of known fields that should trigger the fetch is hardcoded and should be made configurable somehow.
 */
export async function toggleAuditPanel({ keyPrefix = "", panels = [], audit }) {
  const key = `${keyPrefix}${audit?._id}`

  if (!audit) return panels
  if (!Array.isArray(audit?.details)) return panels

  const isCollapsingPanel = panels.includes(key)
  const _panels = isCollapsingPanel ? panels.filter(k => k !== key) : [...panels, key]
  if (isCollapsingPanel) return _panels

  const personRegistrationFields = ["ownerRegistration", "projectDirectorRegistration", "accountManagerRegistration"]

  for (let i = 0; i < audit.details.length; i++) {
    const detail = audit.details[i]
    if (!Array.isArray(detail)) continue

    let shouldSearch = false
    let personRegistration = ""
    let personRegistrationIndex
    for (let j = 0; j < detail.length; j++) {
      if (j === 0) {
        shouldSearch = false
        personRegistration = ""
      }
      const item = detail[j]

      if (typeof item === "string") {
        // This small algorithm is based on the fact that details are ordered in a certain way.
        if (shouldSearch) {
          if (item && item !== "null") {
            personRegistration = item
            personRegistrationIndex = j
          }
        }

        if (personRegistrationFields.includes(item) || item.endsWith("personRegistration")) {
          shouldSearch = true
          continue
        }
      }

      if (typeof item === "object") {
        if (item.personRegistration) {
          personRegistration = item.personRegistration
        }
      }

      if (personRegistration) {
        try {
          const person = await getUriAndCacheResponse(`/api/person/persons/${personRegistration}?projection=firstName,lastName`)
          const personName = `${person.firstName || ""}${person.lastName ? ` ${person.lastName}` : ""}`
          if (!personName) continue

          if (typeof item === "string") {
            audit.details[i][personRegistrationIndex] = `${personRegistration} (${personName})`
          } else {
            item.personRegistration = `${item.personRegistration} (${personName})`
          }
        } catch (error) {
          console.log(error)
        }

        break
      }
    }
  }

  return _panels
}
